import React, { useState, useEffect, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { api } from "../../helper/api.interceptor";
import AddUserModal from "../AddUserModal";
import Swal from "sweetalert2";

const FileManager = () => {
    const [fileManagers, setFileManagers] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState("");
    const [filterStatus, setFilterStatus] = useState("All");

    const changeRef = useRef(Math.random());
    const { id } = useParams();
    const [showAddModal, setShowAddModal] = useState(false);

    // Fetch File Managers List
    const fetchFileManagers = async () => {
        try {
            const data = await api.getAllfileManager();
            console.log(data);

            const sorted = (data || []).sort(
                (a, b) => new Date(b.created_at) - new Date(a.created_at)
            );
            setFileManagers(sorted);
        } catch (error) {
            console.error("Error fetching file managers:", error);
        }
    };

    useEffect(() => {
        fetchFileManagers();
    }, []);

    // Filter logic
    const filteredManagers = fileManagers.filter((fm) => {
        const q = searchQuery.trim().toLowerCase();
        const statusMatch =
            filterStatus === "All" || (fm?.documents?.status || "") === filterStatus;

        return statusMatch && (fm?.username || "").toLowerCase().includes(q);
    });

    // Pagination
    const totalPages = Math.ceil(filteredManagers.length / itemsPerPage);
    const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
    const indexOfLastItem = currentPage * itemsPerPage;
    const currentManagers = filteredManagers.slice(indexOfFirstItem, indexOfLastItem);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(1);
        }
    }, [filteredManagers, currentPage, totalPages]);

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1);
    };

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    const handlePageChange = (page) => setCurrentPage(page);

    // Delete File Manager
    // Delete role from user (only remove File Manager role)
    const handleDeleteClick = async (phone_number) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "This will remove File Manager role from this user.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, remove role!",
            cancelButtonText: "Cancel",
        });

        if (result.isConfirmed) {
            try {
                const payload = {
                    phone_number,
                    role: "File Manager",
                };

                const response = await api.removeUserRole(payload);

                if (response?.status_code === 200) {
                    Swal.fire("Removed!", "Role removed successfully.", "success");
                    fetchFileManagers();
                } else {
                    Swal.fire("Error!", "Failed to remove role.", "error");
                }
            } catch (error) {
                Swal.fire("Error!", error.message, "error");
            }
        }
    };

    return (
        <div>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24">
                <h6 className="fw-semibold mb-0">
                    <Icon icon="mdi:clipboard-list-outline" style={{ width: "30px", height: "30px" }} />
                    {" "} | File Manager
                </h6>

                <ul className="d-flex align-items-center gap-2">
                    <li className="fw-medium">
                        <Link to="/" className="d-flex align-items-center gap-1 hover-text-primary">
                            <Icon icon="solar:home-smile-angle-outline" className="icon text-lg" />
                            Role Management
                        </Link>
                    </li>
                    <li>-</li>
                    <li className="fw-medium">File Manager</li>
                </ul>
            </div>

            <div className="card">
                <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-3">
                    <div className="d-flex align-items-center gap-2">
                        <span>Show</span>
                        <select
                            className="form-select form-select-sm w-auto"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                        >
                            <option value="5">5</option>
                            <option value="10">10</option>
                            <option value="15">15</option>
                            <option value="20">20</option>
                            <option value={filteredManagers.length}>All</option>
                        </select>
                    </div>

                    <div className="d-flex align-items-center gap-3">
                        <div className="icon-field">
                            <input
                                type="text"
                                className="form-control form-control-sm w-auto"
                                placeholder="Search by Name"
                                onChange={handleSearchChange}
                            />
                            <span className="icon">
                                <Icon icon="ion:search-outline" />
                            </span>
                        </div>

                        <button
                            onClick={() => setShowAddModal(true)}
                            className="btn btn-warning rounded-pill fw-medium d-flex align-items-center gap-2"
                        >
                            <Icon icon="mdi:plus" className="fs-5" />
                            Add New File Manager
                        </button>
                    </div>
                </div>

                <div className="card-body" style={{ overflowX: "auto" }}>
                    <table className="table bordered-table mb-0">
                        <thead>
                            <tr>
                                <th>S.L</th>
                                <th>File Manager Name</th>
                                <th>Email</th>
                                <th>Phone Number</th>
                                <th>Warehouse</th>
                                <th>Pincode</th>
                                <th>Action</th>
                            </tr>
                        </thead>

                        <tbody>
                            {currentManagers.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="text-center">
                                        No File Manager found.
                                    </td>
                                </tr>
                            ) : (
                                currentManagers.map((fileManager, index) => (
                                    <tr key={fileManager._id}>
                                        <td>{indexOfFirstItem + index + 1}</td>
                                        <td>{fileManager?.username || "No Name"}</td>
                                        <td>{fileManager?.emailid || "No Email"}</td>
                                        <td>{fileManager?.phone_number || "No Phone"}</td>
                                        <td>{fileManager?.warehouse_name || "N/A"}</td>
                                        <td>{fileManager?.address?.pincode || "N/A"}</td>

                                        <td>
                                            <Link
                                                onClick={() =>
                                                    handleDeleteClick(fileManager.phone_number)
                                                }
                                                className="w-32-px h-32-ppx bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                            >
                                                <Icon icon="mingcute:delete-2-line" />
                                            </Link>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>

                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-24">
                        <span>
                            Showing {indexOfFirstItem + 1} to{" "}
                            {Math.min(indexOfLastItem, filteredManagers.length)} of{" "}
                            {filteredManagers.length} entries
                        </span>

                        <nav>
                            <ul className="pagination">
                                {Array.from({ length: totalPages }, (_, i) => (
                                    <li key={i} className={`page-item ${currentPage === i + 1 ? "active" : ""}`}>
                                        <button className="page-link" onClick={() => handlePageChange(i + 1)}>
                                            {i + 1}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    </div>
                </div>
            </div>

            {showAddModal && (
                <AddUserModal
                    show={showAddModal}
                    onClose={() => setShowAddModal(false)}
                    onUserAdded={fetchFileManagers}
                />
            )}
        </div>
    );
};

export default FileManager;
